import React, {useEffect, useRef, useState} from 'react';
import {KTCard, KTCardBody, KTIcon, toAbsoluteStatic} from "../../../../_mesomb/helpers";
import {Field, Form, Formik, FormikErrors, FormikTouched} from "formik";
import axios, {AxiosRequestConfig} from "axios";
import FormField from "../../../../_mesomb/partials/form/FormField.tsx";
import {camelize, getPaymentMethod, processErrorResponse, random} from "../../../../_mesomb/utils";
import {useTranslation} from "react-i18next";
import * as Yup from "yup";
import i18n from "../../../../_mesomb/i18n";
import {TrxSocket} from "../../../../_mesomb/utils/TrxSocket.ts";
import ButtonIndicator from "../../../../_mesomb/partials/components/ButtonIndicator.tsx";
import Swal from "sweetalert2";
import HIcon from "../../payment/components/HIcon.tsx";
import {Helmet} from "react-helmet";
import Toast from 'react-bootstrap/Toast';
import {PaymentLinkModel, TransactionPerformResponse} from "../../../../types";
import {useThemeMode} from "../../../../_mesomb/partials/layout/theme-mode/ThemeModeProvider.tsx";
import {ThemeModeComponent} from "../../../../_mesomb/assets/ts/layout";
import {fullNameFieldSchema} from "../../../../_mesomb/partials/form/FullNameField.tsx";
import {addressFieldSchema} from "../../../../_mesomb/partials/form/AddressInput.tsx";
import {contactFieldSchema} from "../../../../_mesomb/partials/form/ContactField.tsx";
import {InfoStroke16} from "../../../../_mesomb/assets/icons/InfoStroke16.tsx";
import {CheckCircleOne} from "../../../../_mesomb/assets/icons/CheckCircleOne.tsx";
import parse from 'url-parse';

let socket: TrxSocket;

const productSchema = Yup.object().shape({
  product: Yup.string().required(i18n.t('Field_is_required')),
  quantity: Yup.number().required(i18n.t('Field_is_required')).min(1, i18n.t('Invalid quantity')),
});

const formSchema = Yup.object().shape({
  service: Yup.string().oneOf(['MTN', 'ORANGE', 'AIRTEL'])
    .required(i18n.t('Please select a payment provider')),
  payer: Yup.string()
    .required(i18n.t('Field_is_required'))
    .matches(/^[+]?\d{8,12}$/, i18n.t('Invalid phone number')),
  contact: contactFieldSchema,
  full_name: fullNameFieldSchema,
  address: addressFieldSchema,
  // custom_fields: Yup.array().of(Yup.string()),
  products: Yup.array().of(productSchema),
  amount: Yup.number().required(i18n.t('Field_is_required')).min(1, i18n.t('Invalid amount')),
  custom_fields: Yup.array().of(Yup.string()),
})
type FormPayWithLinkValues = Yup.InferType<typeof formSchema>

const sendLog = (link: Pick<PaymentLinkModel, 'id'>, requestId: string) => {
  axios.post('logs/http/', {
    method: 'GET',
    path: location.pathname,
    request_id: requestId,
    status_code: 200,
    target: {
      model: 'payment.PaymentLink',
      id: link.id,
    }
  })
}

const systemMode = ThemeModeComponent.getSystemMode() as 'light' | 'dark';

function PayWithLink({link, isDemo = false}: {link?: Omit<PaymentLinkModel, 'created_at' | 'url' | 'stats' | 'details' >, isDemo?: boolean}) {
  const {mode} = useThemeMode()
  const calculatedMode = mode === 'system' ? systemMode : mode

  const parsed = parse(location.href, true);
  const urlParams = parsed.query;

  const {t} = useTranslation()

  if (!link) {
    return (
      <KTCard flush={true} className={'w-lg-900px'}>
        <KTCardBody>
          {t('Sorry payment link you ask was not found!')}
        </KTCardBody>
      </KTCard>
    )
  }

  const requestId = useRef(random())
  const hasSendLog = useRef(false);
  const [showToast, setShowToast] = useState<{display: boolean, service?: string}>({display: false})
  const [finished, setFinished] = useState(false)

  useEffect(() => {
    if (link.source != 'external' && !isDemo) {
      sendLog(link, requestId.current)
      hasSendLog.current = false;
    }
    if (urlParams.locale) {
      i18n.changeLanguage(urlParams.locale)
    }
    return () => {
      socket?.closeSocket();
    }
  }, []);

  const providers = getPaymentMethod('CM')
  const excludes: (keyof FormPayWithLinkValues)[] = [];
  if (!link.collect_customer_address) {
    excludes.push('address')
  }
  if (!link.required_contact_info) {
    excludes.push('contact')
  }
  if (link.type !== 'ECOMMERCE') {
    excludes.push('products')
  }
  if (link.type !== 'PAYMENT') {
    excludes.push('amount')
  }

  if (link.status !== 'ACTIVE') {
    let message = t('Sorry payment link you ask is not active!');
    if (link.limit_payments.enabled && (link.limit_payments.used ?? 0) >= (link.limit_payments.number ?? 0) && link.limit_payments.message) {
      message = link.limit_payments.message;
    } else if (link.status_message) {
      message = link.status_message;
    }
    return (
      <KTCard flush={true} className={'w-lg-600px'}>
        <KTCardBody className={'py-xxl-20 text-center'}>
          <InfoStroke16 />
          <div className={'mt-3'}>
            {message}
          </div>
        </KTCardBody>
      </KTCard>
    )
  }

  if (finished) {
    return (
      <KTCard flush={true} className={'w-lg-700px'}>
        <KTCardBody className={'py-xxl-20 text-center'}>
          <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={() => setFinished(false)}>
            <KTIcon className='fs-1' iconName='cross'/>
          </div>
          <div className="text-center">
            <CheckCircleOne width={40} height={40} color={'green'} />
            <h1 className="text-dark fw-bolder fs-1x my-5">{t('Payment successful!')}</h1>
            <div className="text-muted fw-semibold fs-1 mt-3">{link.confirmation?.message ?? t('Thank you for your payment')}</div>
          </div>
        </KTCardBody>
      </KTCard>
    )
  }

  return (
    <>
      <KTCard flush={true} className={'w-lg-900px'}>
        <KTCardBody>
          <Formik<FormPayWithLinkValues>
            validationSchema={formSchema.omit(excludes)}
            initialValues={{
              service: 'ORANGE',
              products: (link.products ?? [])?.map(product => ({
                product: String(product.id),
                quantity: product.quantity
              })),
              amount: (link.type === 'PAYMENT' ? link.amount?.value : link.price) ?? 0,
              payer: '',
              full_name: {
                last_name: '',
              },
              contact: {
                email: urlParams.email
              },
              address: {
                country: 'CM',
                region: '',
                town: '',
              },
              // custom_fields: (link.custom_fields || []).map(c => '')
            }}
            validateOnMount={false}
            validateOnBlur={false}
            // validateOnChange={false}
            onSubmit={(values, {setSubmitting, resetForm, setErrors}) => {
              setSubmitting(true)
              const processTrxResponse = (ret: TransactionPerformResponse) => {
                const status = ret.transaction?.status ?? ret.status;
                // var transaction = ret.transaction;
                if (['SUCCESS', 'FAILED'].includes(status)) {
                  resetForm();
                  setSubmitting(false)
                  const isSuccess = status === 'SUCCESS';


                  let message = isSuccess ? t('Default_success_transaction_message') : t('Default_failure_transaction_message');
                  if (isSuccess && link.confirmation?.message) {
                    message = link.confirmation.message;
                  }

                  if (ret.redirect && status === 'SUCCESS') {
                    window.location.href = ret.redirect;
                  } else {
                    Swal.fire({
                      title: isSuccess ? t('Confirmation!') : t('Failure!'),
                      text: message,
                      icon: isSuccess ? 'success' : 'error',
                      buttonsStyling: false,
                      confirmButtonText: t('Ok, got it!'),
                      customClass: {
                          confirmButton: "btn fw-bold btn-light-primary"
                      },
                      background: calculatedMode == 'dark' ? 'rgb(21, 23, 28)' : undefined,
                    })
                  }
                  socket.closeSocket();
                }
              }
              socket = new TrxSocket(requestId.current, processTrxResponse);
              if (!hasSendLog.current && link.source != 'external' && !isDemo) {
                sendLog(link, requestId.current)
                hasSendLog.current = true;
              }
              const configs: AxiosRequestConfig<FormPayWithLinkValues> = {};

              configs.headers = {};
              const input: FormPayWithLinkValues & Record<string, unknown> = {...values};
              for (const exclude of excludes) {
                delete input[exclude];
              }

              if (link.source === 'external') {
                input.service = values.service;
                configs.headers['X-MeSomb-Callback'] = link.callback;
                if (link.trxID) {
                  configs.headers['X-MeSomb-TrxID'] = link.trxID;
                }
                if (link.nonce) {
                  configs.headers['X-MeSomb-Nonce'] = link.nonce;
                }
                if (link.data?.products) {
                  input.products = link.data?.products;
                }
                // if (link.data?.customer) {
                //   values.customer = link.data?.customer;
                // }
                // if (link.data?.location) {
                //   values.location = link.data?.location;
                // }
              }
              configs.headers['X-MeSomb-RequestID'] = requestId.current;
              configs.headers['X-MeSomb-OperationMode'] = 'asynchronous';

              input.urlparams = urlParams;

              setShowToast({display: true, service: values.service})
              axios.post(`/payment/${link.source || 'pay'}/${link.key}/`, input, configs).then(({data}) => {
                processTrxResponse(data);
                requestId.current = random();
                hasSendLog.current = false;
              }).catch((error) => {
                setSubmitting(false)
                processErrorResponse(error, setErrors)
                requestId.current = random();
              })
            }}>
            {({isSubmitting, isValid, touched, errors, values}) => {
              return (
                <Form noValidate>
                  <div className="row">
                    <div className="col-lg-6" id="products_details">
                      {link.application && (
                        <>
                          <div className="d-flex align-items-center mb-10">
                            <a href="#" className="btn btn-icon btn-secondary h-30px w-30px me-2">
                              <HIcon image={link.application.logo?.path} size={30} text={link.application.name}/>
                            </a>
                            <span className="fw-semibold font-size-lg">{link.application.name}</span>
                          </div>
                        </>
                      )}
                      {link.type == 'PAYMENT' ? (
                        <>
                          <div>
                            <label className="text-gray-600 fw-semibold fs-4">{link.title}</label>
                          </div>
                          <div>
                            {link.amount?.editable === false ? (
                              <div>
                                <span className="text-gray-900 fw-bold fs-2hx">{t('intlCurrency', {value: link.amount.value, currency: link.currency ?? 'XAF'})}</span>
                                <Field type={'hidden'} name={'amount'} />
                              </div>
                            ) : (
                              <FormField
                                type={'amount'}
                                name={'amount'}
                                error={errors.amount}
                                touched={touched.amount}
                                placeholder={t('Amount')}
                                readOnly={isDemo}
                                prefix={'FCFA '}
                              />
                            )}
                          </div>
                          {link.description && (
                            <div>
                              <span className="fw-light text-gray-800">{link.description}</span>
                            </div>
                          )}
                          {link.image_url && <div
                            className="d-flex flex-row-fluid bgi-no-repeat bgi-position-x-center bgi-size-cover card-rounded h-150px h-lg-250px mb-lg-20 me-lg-20 mt-lg-10"
                            style={{backgroundImage: `url(${toAbsoluteStatic(link.image_url)})`}}></div>}
                        </>
                      ) : (
                        <>
                          {link.products?.length === 1 && !link.products[0].adjustable ? (
                            <>
                              <div>
                                <span className="text-gray-600 fw-semibold fs-4">{link.products[0].product?.name}</span>
                              </div>
                              <div>
                                <span className="text-black fw-bold fs-2hx">{t('intlCurrency', {
                                  value: link.products[0].subtotal,
                                  currency: link.products[0].product?.default_price_currency ?? 'XAF'
                                })}</span>
                              </div>
                              {link.products[0].quantity > 1 && (
                                <div>
                                  <span className="fw-light text-gray-800">{t('Qty_per_each', {
                                    quantity: link.products[0].quantity,
                                    price: link.products[0].product?.default_price
                                  })}</span>
                                </div>
                              )}
                              {link.products[0].product?.image_url && (
                                <div
                                  style={{backgroundImage: `url(${toAbsoluteStatic(link.products[0].product?.image_url)})`}}
                                  className="d-flex flex-row-fluid bgi-no-repeat bgi-position-x-center bgi-size-cover card-rounded h-150px h-lg-250px mb-lg-20 me-lg-20 mt-lg-10"/>
                              )}
                            </>
                          ) : (
                            <>
                              <div>
                                <label
                                  data-cy={'payment-price'}
                                  className="fw-bold fs-3x">{t('intlNumber', {value: values.products?.reduce((acc, prod, i) => acc + (prod.quantity * parseFloat(link.products[i].product?.default_price ?? '0')), 0)})}</label>
                              </div>
                              <table className="table align-middle table-row-dashed fs-6 gy-5 mb-0 mt-5">
                                <tbody className="fw-semibold text-gray-600">

                                {values.products?.map((prod, i) => {
                                  let productError: FormikErrors<Yup.InferType<typeof productSchema>> = {};
                                  let productTouched: FormikTouched<Yup.InferType<typeof productSchema>> = {};
                                  if ((errors.products ?? []).length > i) {
                                    productError = errors.products![i]! as FormikErrors<Yup.InferType<typeof productSchema>>;
                                  }
                                  if (((touched.products ?? []) as FormikTouched<Yup.InferType<typeof productSchema>>[]).length > i) {
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-expect-error
                                    productTouched = (touched.products)![i]!;
                                  }

                                  return (
                                    <tr>
                                      <td>
                                        <div className="d-flex align-items-center">
                                          <Field
                                            type={'hidden'}
                                            name={`products[${i}].product`}
                                            errors={errors}
                                            touched={touched}
                                          />
                                          {link.products[i].product.image_url && (
                                            <a href="#" className="symbol symbol-50px">
                                                  <span className="symbol-label"
                                                        style={{backgroundImage: `url(${toAbsoluteStatic(link.products[i].product.image_url)})`}}></span>
                                            </a>
                                          )}
                                          <div className="ms-5">
                                            <div
                                              className="fw-semibold">{link.products[i].product.name}</div>
                                            <div
                                              className="fw-light fs-7">{link.products[i].product.description}</div>
                                            {link.products[i].adjustable ? (
                                              <div className={'mt-1'}>
                                                <FormField
                                                  type={'number'}
                                                  name={`products[${i}].quantity`}
                                                  error={productError?.quantity}
                                                  touched={productTouched?.quantity}
                                                  size={'sm'}
                                                  marginBottom={0}
                                                />
                                              </div>
                                            ) : (
                                              <div>
                                                <Field
                                                  type={'hidden'}
                                                  name={`products[${i}].quantity`}
                                                  error={productError?.quantity}
                                                  touched={productTouched?.quantity}
                                                />
                                                <div>
                                                <span
                                                  className="fw-light text-gray-800">Qty {t('intlNumber', {value: prod.quantity})}</span>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="text-end">
                                            <span>{t('intlCurrency', {
                                              value: prod.quantity * parseFloat(link.products[i].product.default_price ?? '0'),
                                              currency: link.products[i].product.default_price_currency ?? 'XAF'
                                            })}</span>
                                        <div className="mt-4 fs-7 fw-light">{t('intlCurrency', {
                                          value: link.products[i].product.default_price,
                                          currency: link.products[i].product.default_price_currency ?? 'XAF'
                                        })} {t('each')}</div>
                                      </td>
                                    </tr>
                                  );
                                })}
                                </tbody>
                              </table>
                            </>
                          )}
                        </>
                      )}
                      {link.extra_fees?.value && link.extra_fees?.enabled && (
                        <div className="mt-1">
                            <span className="lh-sm text-muted">
                              {t('Extra fees of ')}<span
                              className="fw-bold fs-3">{t(link.extra_fees.type == 'FIXED' ? 'intlCurrency' : 'intlNumber', {value: link.extra_fees.value, currency: 'XAF'})}{link.extra_fees.type == 'FIXED' ? '' : '%'}</span> {t('will be added for')}
                              <span className={'fw-light fw-bold'}> {link.extra_fees.reason}</span>
                            </span>
                        </div>
                      )}
                    </div>
                    <div className={'col-lg-6'}>
                      {link.required_contact_info && (
                        <>
                          <h4 className="mb-2">{t('Contact Information')}</h4>
                          <FormField
                            type={'contact'}
                            name={'contact'}
                            error={errors.contact}
                            touched={touched.contact}
                            readOnly={isDemo}
                          />
                        </>
                      )}

                      {link.custom_fields?.map((field, i) => (
                        <FormField
                          type={field.type}
                          name={`custom_fields[${i}]`}
                          error={errors.custom_fields ? errors.custom_fields[i] : undefined}
                          touched={touched.custom_fields ? touched.custom_fields[i] === true : undefined}
                          label={field.label}
                          required={!field.optional}
                          validate={(value) => {
                            if (!field.optional && !value) {
                              return t('Field_is_required')
                            }
                            if (field.type === 'number' && !/^\d+$/.test(value as string)) {
                              return t('Invalid number')
                            }
                            if (!value) {
                              return
                            }

                            if (field.set_limit) {
                              if (field.type == 'text') {
                                if (field.limit_min && (value as string).length < field.limit_min) {
                                  return t('Minimum_characters', {min: field.limit_min})
                                }
                                if (field.limit_max && (value as string).length > field.limit_max) {
                                  return t('Maximum_characters', {max: field.limit_max})
                                }
                              } else {
                                if (field.limit_min && (value as unknown as number) < field.limit_min) {
                                  return t('Minimum_value', {min: field.limit_min})
                                }
                                if (field.limit_max && (value as unknown as number) > field.limit_max) {
                                  return t('Maximum_value', {max: field.limit_max})
                                }
                              }
                            }
                          }}
                        />
                      ))}

                      <h4 className="mb-2 mt-7">{t('Payment Method')}</h4>
                      <FormField
                        type={'megaOption'}
                        name={'service'}
                        error={errors.service}
                        touched={touched.service}
                        options={providers.map(p => ({
                          value: p.key,
                          label: p.service_name,
                          image: toAbsoluteStatic(p.logo)
                        }))}
                        readOnly={isDemo}
                      />
                      <FormField
                        type={'phone'}
                        name={'payer'}
                        error={errors.payer}
                        touched={touched.payer}
                        placeholder={'670000000'}
                        readOnly={isDemo}
                        showCountryCode={false}
                      />

                      <FormField
                        type={'full_name'}
                        name={'full_name'}
                        error={errors.full_name}
                        touched={touched.full_name}
                        readOnly={isDemo}
                      />

                      {link.collect_customer_address && (
                        <FormField
                          type={'address'}
                          name={'address'}
                          error={errors.address}
                          touched={touched.address}
                          readOnly={isDemo}
                        />
                      )}

                      <ButtonIndicator
                        data-cy={'submit-button'}
                        type={'submit'}
                        label={t(camelize(link.call_to_action))}
                        loading={isSubmitting}
                        disabled={isSubmitting || !isValid || !touched}
                      />

                      {showToast.display && (
                        <Toast
                          onClose={() => setShowToast({display: false})}
                          show={showToast.display}
                          delay={5000}
                          autohide={true}
                          bg={'light'}
                          animation={true}
                          style={{width: '100%'}}
                          className={'mt-2'}
                        >
                          <Toast.Body className={'p-2 fs-5'}>{t('Check_phone_validation', {ussd: {'ORANGE': '#150*50#', 'MTN': '*126#'}[showToast.service!]})}</Toast.Body>
                        </Toast>
                      )}
                    </div>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </KTCardBody>
      </KTCard>
      <Helmet>
        <title>{t('Pay_on_app', {application: link.application?.name})}</title>
        <meta name={'og:title'} content={t('Pay_on_app', {application: link.application?.name})}/>
        <meta name={'og:image'} content={`http://localhost:8000${link.image_url}`}/>
        <meta name={'og:description'} content={link.description}/>
      </Helmet>
    </>
  );
}

export default PayWithLink;
